import React, { Component } from 'react';
import { DebounceInput } from 'react-debounce-input';
import axios from 'axios';
import PropTypes from 'prop-types';
import PasswordRevealWithoutForwardedType from './PasswordRevealWithoutForwardedType';
import ErrorHandler from '../errorHandler/errorHandler';
import StrengthMeter from '../strength-meter/StrengthMeter';
import PasswordRules from '../password-rules/PasswordRules';

const propTypes = {
    validateUrl: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    defaultPassword: PropTypes.string,
    isRegistered: PropTypes.bool
};

const Errors = {
    passwordsDontMatch: 'Passwords entered do not match.'
};

const Strengths = {
    0: 'Weak',
    1: 'Fair',
    2: 'Strong',
    Weak: 0,
    Fair: 1,
    Strong: 2
};

class NewPasswordContainer extends Component {
    constructor(props) {
        super(props);

        const initialProposed = props.defaultPassword ?? '';

        this.proposedPasswordRef = initialProposed;
        this.confirmPasswordRef = '';

        this.state = {
            strength: null,
            rules: [],
            passwordsMatch: false,
            errorMessage: null,
            isValid: null
        };

        this.handlePWInput = this.handlePWInput.bind(this);
        this.handleConfirmInput = this.handleConfirmInput.bind(this);
        // The int indicated by a service of "okay" strength
        this.strengthThreshold = 1;

        const canUseDOM = Boolean(
            typeof window !== 'undefined' && window.document && window.document.createElement
        );
        if (canUseDOM) {
            this.fetchRules();
        }
    }

    notifyParentValidity() {
        const { onChange } = this.props;
        if (typeof onChange !== 'function') {
            return;
        }
        onChange(this.state.isValid === true && this.state.passwordsMatch);
    }

    /**
     * Compare passwords from refs so match state stays correct across debounced
     * handlers and before React commits setState from the prior field.
     */
    updateMatchStateFromRefs() {
        const proposed = this.proposedPasswordRef ?? '';
        const confirm = this.confirmPasswordRef ?? '';

        let passwordsMatch = false;
        let errorMessage = null;

        if (proposed.length === 0 || confirm.length === 0) {
            passwordsMatch = false;
            errorMessage = null;
        } else {
            passwordsMatch = proposed === confirm;
            errorMessage = passwordsMatch ? null : Errors.passwordsDontMatch;
        }

        this.setState({ passwordsMatch, errorMessage }, () => this.notifyParentValidity());
    }

    handlePWInput(event) {
        this.proposedPasswordRef = event.target.value;
        this.validatePassword(event.target.value);
        this.updateMatchStateFromRefs();
    }

    handleConfirmInput(event) {
        this.confirmPasswordRef = event.target.value;
        this.updateMatchStateFromRefs();
    }

    fetchRules() {
        axios
            .post(this.props.validateUrl, { password: '' })
            .then(response => {
                if (response.data && response.data.status) {
                    this.setState({
                        rules: response.data.status
                    });
                }
            })
            .catch(function (error) {
                var errLog = new ErrorHandler();
                errLog.logError(error);
            });
    }
    validatePassword(pw) {
        const that = this; // passed on to promise

        axios
            .post(this.props.validateUrl, { password: pw })
            .then(function (response) {
                if (pw !== that.proposedPasswordRef) {
                    return;
                }

                if (response.data !== null) {
                    // If empty, null strength (hides meter)
                    let strength = pw.length === 0 ? null : response.data.passwordStrength;

                    const isValid = strength >= that.strengthThreshold;

                    that.setState(
                        {
                            strength,
                            rules: response.data.status,
                            isValid
                        },
                        () => that.notifyParentValidity()
                    );
                }
            })
            .catch(function (error) {
                var errLog = new ErrorHandler();
                errLog.logError(error);
            });
    }

    render() {
        let { errorMessage, strength, rules } = this.state;
        let modifyingClass = '';
        let errorElem;
        let labelPassword = this.props.isRegistered ? 'New Password' : 'Password';
        let labelConfirmPassword = this.props.isRegistered
            ? 'Confirm New Password'
            : 'Confirm Password';

        let ruleElems = (
            <div className="asc-form-group">
                <p className="asc-sub-heading-small">Password Rules</p>
                <p className="asc-form-subtext">Satisfy 3 of 4</p>
                <PasswordRules rulesStatus={rules} showStatus={strength !== null} />
            </div>
        );

        if (errorMessage && errorMessage.length > 0) {
            errorElem = <span className="asc-form-help">{errorMessage}</span>;
            modifyingClass = 'is-warning';
        }

        return (
            <fieldset className={'asc-form-group ' + modifyingClass}>
                <DebounceInput
                    element={PasswordRevealWithoutForwardedType}
                    onChange={this.handlePWInput}
                    debounceTimeout={700}
                    forceNotifyOnBlur
                    placeholder={labelPassword}
                    id="password"
                    name="password"
                    defaultValue={this.props.defaultPassword}
                    pattern=".{8,}"
                    title="8 or more characters"
                    subtext="8 or more characters"
                    isNewPassword
                    isRequired
                    label={labelPassword}
                    required
                />

                <StrengthMeter strength={strength} dict={Strengths} />
                {ruleElems}
                <DebounceInput
                    element={PasswordRevealWithoutForwardedType}
                    onChange={this.handleConfirmInput}
                    debounceTimeout={700}
                    forceNotifyOnBlur
                    label={labelConfirmPassword}
                    placeholder={labelConfirmPassword}
                    id="confirmPassword"
                    name="confirmPassword"
                    pattern=".{8,}"
                    title="8 or more characters"
                    isNewPassword
                    isRequired
                    required
                />
                {errorElem}
            </fieldset>
        );
    }
}

NewPasswordContainer.defaultProps = {
    onChange: () => {}
};

NewPasswordContainer.propTypes = propTypes;
export default NewPasswordContainer;
